import {
  WorkflowDefinition,
  WorkflowStep,
  StepType,
  StepStatus,
  WorkflowTrigger
} from './workflow-engine';
import { v4 as uuidv4 } from 'uuid';

// 模板参数
export interface TemplateParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  description?: string;
  required?: boolean;
  default?: any;
  enum?: any[];
}

// 工作流模板
export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  parameters?: TemplateParameter[];
  definition: WorkflowDefinition;
  examples?: Record<string, any>[];
}

// 模板类别
export enum TemplateCategory {
  DEVELOPMENT = 'development',
  BUGFIX = 'bugfix',
  RELEASE = 'release',
  MAINTENANCE = 'maintenance',
  DEVOPS = 'devops',
  QUALITY = 'quality'
}

// 工作流模板库
export class WorkflowTemplateLibrary {
  private templates = new Map<string, WorkflowTemplate>();
  
  constructor() {
    // 注册内置模板
    this.registerBuiltinTemplates();
  }
  
  /**
   * 注册模板
   */
  register(template: WorkflowTemplate): void {
    this.templates.set(template.id, template);
  }
  
  /**
   * 获取模板
   */
  get(templateId: string): WorkflowTemplate | undefined {
    return this.templates.get(templateId);
  }
  
  /**
   * 获取所有模板
   */
  getAll(category?: string): WorkflowTemplate[] {
    let templates = Array.from(this.templates.values());
    
    if (category) {
      templates = templates.filter(t => t.category === category);
    }
    
    return templates;
  }
  
  /**
   * 从模板创建工作流定义
   */
  createFromTemplate(
    templateId: string,
    parameters: Record<string, any> = {}
  ): WorkflowDefinition {
    const template = this.get(templateId);
    if (!template) {
      throw new Error(`Template not found: ${templateId}`);
    }
    
    // 验证参数
    this.validateParameters(template, parameters);
    
    // 深拷贝定义
    const definition = JSON.parse(JSON.stringify(template.definition));
    
    // 应用参数
    this.applyParameters(definition, parameters);
    
    // 生成新ID
    definition.id = uuidv4();
    
    return definition;
  }
  
  /**
   * 验证参数
   */
  private validateParameters(
    template: WorkflowTemplate,
    parameters: Record<string, any>
  ): void {
    if (!template.parameters) return;
    
    for (const param of template.parameters) {
      const value = parameters[param.name];
      
      // 检查必需参数
      if (param.required && value === undefined) {
        throw new Error(`Missing required parameter: ${param.name}`);
      }
      
      // 检查类型
      if (value !== undefined) {
        const actualType = Array.isArray(value) ? 'array' : typeof value;
        if (actualType !== param.type) {
          throw new Error(`Invalid type for parameter "${param.name}": expected ${param.type}, got ${actualType}`);
        }
        
        // 检查枚举值
        if (param.enum && !param.enum.includes(value)) {
          throw new Error(`Invalid value for parameter "${param.name}": must be one of ${param.enum.join(', ')}`);
        }
      }
    }
  }
  
  /**
   * 应用参数到定义
   */
  private applyParameters(
    definition: WorkflowDefinition,
    parameters: Record<string, any>
  ): void {
    const replace = (obj: any): any => {
      if (typeof obj === 'string') {
        // 替换参数占位符 ${paramName}
        return obj.replace(/\$\{([^}]+)\}/g, (match, paramName) => {
          return parameters[paramName] ?? match;
        });
      }
      
      if (Array.isArray(obj)) {
        return obj.map(replace);
      }
      
      if (obj && typeof obj === 'object') {
        const result: any = {};
        for (const [key, value] of Object.entries(obj)) {
          result[key] = replace(value);
        }
        return result;
      }
      
      return obj;
    };
    
    // 递归替换所有参数
    definition.name = replace(definition.name);
    definition.description = replace(definition.description);
    definition.steps = replace(definition.steps);
    definition.variables = replace(definition.variables);
  }
  
  /**
   * 注册内置模板
   */
  private registerBuiltinTemplates(): void {
    // 1. Bug 修复工作流
    this.register({
      id: 'bugfix-standard',
      name: 'Standard Bug Fix',
      description: 'Complete bug fix workflow with reproduction, fix, and verification',
      category: TemplateCategory.BUGFIX,
      parameters: [
        {
          name: 'issueId',
          type: 'string',
          description: 'Issue ID or ticket number',
          required: true
        },
        {
          name: 'severity',
          type: 'string',
          description: 'Bug severity',
          enum: ['critical', 'high', 'medium', 'low'],
          default: 'medium'
        }
      ],
      definition: {
        id: 'bugfix-standard',
        name: 'Bug Fix: ${issueId}',
        version: '1.0.0',
        steps: [
          {
            id: 'reproduce',
            name: 'Reproduce Issue',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: [],
            actions: [
              {
                type: 'prompt',
                name: 'confirm-reproduction',
                params: {
                  message: 'Have you successfully reproduced the issue?',
                  type: 'confirm'
                }
              }
            ]
          },
          {
            id: 'analyze',
            name: 'Root Cause Analysis',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['reproduce'],
            actions: [
              {
                type: 'command',
                name: 'analyze-codebase',
                params: {
                  search: 'related code'
                }
              }
            ]
          },
          {
            id: 'implement-fix',
            name: 'Implement Fix',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['analyze'],
            actions: [
              {
                type: 'command',
                name: 'create-branch',
                params: {
                  name: 'fix/${issueId}'
                }
              }
            ]
          },
          {
            id: 'test',
            name: 'Test Fix',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['implement-fix'],
            actions: [
              {
                type: 'command',
                name: 'run-tests'
              }
            ],
            retryable: true,
            maxRetries: 3
          },
          {
            id: 'verify',
            name: 'Verify Fix',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['test'],
            actions: [
              {
                type: 'prompt',
                name: 'confirm-fix',
                params: {
                  message: 'Does the fix resolve the issue without side effects?'
                }
              }
            ]
          },
          {
            id: 'review',
            name: 'Code Review',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['verify'],
            actions: [
              {
                type: 'command',
                name: 'create-pr',
                params: {
                  title: 'Fix: ${issueId}',
                  labels: ['bugfix', '${severity}']
                }
              }
            ]
          }
        ]
      }
    });
    
    // 2. 功能开发工作流
    this.register({
      id: 'feature-development',
      name: 'Feature Development',
      description: 'Complete feature development workflow from design to deployment',
      category: TemplateCategory.DEVELOPMENT,
      parameters: [
        {
          name: 'featureName',
          type: 'string',
          required: true
        },
        {
          name: 'requiresDesign',
          type: 'boolean',
          default: true
        }
      ],
      definition: {
        id: 'feature-development',
        name: 'Feature: ${featureName}',
        version: '1.0.0',
        steps: [
          {
            id: 'requirements',
            name: 'Gather Requirements',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: [],
            actions: [
              {
                type: 'command',
                name: 'create-doc',
                params: {
                  template: 'requirements',
                  name: '${featureName}'
                }
              }
            ]
          },
          {
            id: 'design',
            name: 'Technical Design',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['requirements'],
            conditions: [
              {
                type: 'expression',
                expression: '$requiresDesign == true'
              }
            ],
            actions: [
              {
                type: 'command',
                name: 'create-design-doc'
              }
            ]
          },
          {
            id: 'task-breakdown',
            name: 'Task Breakdown',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['design'],
            actions: [
              {
                type: 'command',
                name: 'create-tasks'
              }
            ]
          },
          {
            id: 'implementation',
            name: 'Implementation',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['task-breakdown'],
            actions: [
              {
                type: 'command',
                name: 'track-progress'
              }
            ]
          },
          {
            id: 'testing',
            name: 'Testing',
            type: StepType.PARALLEL,
            status: StepStatus.PENDING,
            dependencies: ['implementation'],
            parallel: true,
            actions: [
              {
                type: 'command',
                name: 'run-unit-tests'
              },
              {
                type: 'command',
                name: 'run-integration-tests'
              }
            ]
          },
          {
            id: 'documentation',
            name: 'Documentation',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['implementation'],
            parallel: true,
            actions: [
              {
                type: 'command',
                name: 'update-docs'
              }
            ]
          },
          {
            id: 'review',
            name: 'Final Review',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['testing', 'documentation'],
            actions: [
              {
                type: 'command',
                name: 'create-pr'
              }
            ]
          }
        ]
      }
    });
    
    // 3. 紧急修复工作流
    this.register({
      id: 'hotfix-emergency',
      name: 'Emergency Hotfix',
      description: 'Fast-track workflow for critical production issues',
      category: TemplateCategory.BUGFIX,
      parameters: [
        {
          name: 'incidentId',
          type: 'string',
          required: true
        },
        {
          name: 'affectedService',
          type: 'string',
          required: true
        }
      ],
      definition: {
        id: 'hotfix-emergency',
        name: 'Hotfix: ${incidentId}',
        version: '1.0.0',
        variables: {
          skipTests: false,
          notifyTeam: true
        },
        steps: [
          {
            id: 'assess',
            name: 'Assess Impact',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: [],
            actions: [
              {
                type: 'command',
                name: 'check-metrics',
                params: {
                  service: '${affectedService}'
                }
              }
            ],
            timeout: 300000 // 5 minutes
          },
          {
            id: 'quick-fix',
            name: 'Implement Quick Fix',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['assess'],
            actions: [
              {
                type: 'command',
                name: 'create-hotfix-branch'
              }
            ]
          },
          {
            id: 'minimal-test',
            name: 'Minimal Testing',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['quick-fix'],
            conditions: [
              {
                type: 'expression',
                expression: '$skipTests != true'
              }
            ],
            actions: [
              {
                type: 'command',
                name: 'run-smoke-tests'
              }
            ]
          },
          {
            id: 'deploy',
            name: 'Emergency Deploy',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['minimal-test'],
            actions: [
              {
                type: 'command',
                name: 'deploy-hotfix',
                params: {
                  environment: 'production',
                  service: '${affectedService}'
                }
              }
            ]
          },
          {
            id: 'verify-fix',
            name: 'Verify in Production',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['deploy'],
            actions: [
              {
                type: 'command',
                name: 'monitor-metrics'
              }
            ]
          },
          {
            id: 'followup',
            name: 'Schedule Follow-up',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['verify-fix'],
            actions: [
              {
                type: 'command',
                name: 'create-followup-task',
                params: {
                  description: 'Proper fix and tests for ${incidentId}'
                }
              }
            ]
          }
        ]
      }
    });
    
    // 4. 代码审查工作流
    this.register({
      id: 'code-review',
      name: 'Code Review Process',
      description: 'Structured code review workflow',
      category: TemplateCategory.QUALITY,
      parameters: [
        {
          name: 'prNumber',
          type: 'string',
          required: true
        },
        {
          name: 'reviewers',
          type: 'array',
          default: []
        }
      ],
      definition: {
        id: 'code-review',
        name: 'Review PR #${prNumber}',
        version: '1.0.0',
        steps: [
          {
            id: 'automated-checks',
            name: 'Automated Checks',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: [],
            actions: [
              {
                type: 'command',
                name: 'run-linter'
              },
              {
                type: 'command',
                name: 'run-tests'
              },
              {
                type: 'command',
                name: 'check-coverage'
              }
            ]
          },
          {
            id: 'assign-reviewers',
            name: 'Assign Reviewers',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['automated-checks'],
            actions: [
              {
                type: 'command',
                name: 'assign-reviewers',
                params: {
                  reviewers: '${reviewers}'
                }
              }
            ]
          },
          {
            id: 'code-review',
            name: 'Code Review',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['assign-reviewers'],
            actions: [
              {
                type: 'prompt',
                name: 'review-checklist',
                params: {
                  items: [
                    'Code follows project conventions',
                    'Tests are adequate',
                    'Documentation is updated',
                    'No security issues',
                    'Performance impact considered'
                  ]
                }
              }
            ]
          },
          {
            id: 'address-feedback',
            name: 'Address Feedback',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['code-review'],
            actions: [
              {
                type: 'command',
                name: 'list-review-comments'
              }
            ]
          },
          {
            id: 'final-approval',
            name: 'Final Approval',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['address-feedback'],
            actions: [
              {
                type: 'command',
                name: 'approve-pr'
              }
            ]
          }
        ]
      }
    });
    
    // 5. 发布准备工作流
    this.register({
      id: 'release-preparation',
      name: 'Release Preparation',
      description: 'Prepare for production release',
      category: TemplateCategory.RELEASE,
      parameters: [
        {
          name: 'version',
          type: 'string',
          required: true
        },
        {
          name: 'releaseType',
          type: 'string',
          enum: ['major', 'minor', 'patch'],
          default: 'minor'
        }
      ],
      definition: {
        id: 'release-preparation',
        name: 'Release v${version}',
        version: '1.0.0',
        steps: [
          {
            id: 'version-bump',
            name: 'Version Bump',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: [],
            actions: [
              {
                type: 'command',
                name: 'bump-version',
                params: {
                  version: '${version}',
                  type: '${releaseType}'
                }
              }
            ]
          },
          {
            id: 'changelog',
            name: 'Update Changelog',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['version-bump'],
            actions: [
              {
                type: 'command',
                name: 'generate-changelog'
              }
            ]
          },
          {
            id: 'test-suite',
            name: 'Full Test Suite',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['changelog'],
            actions: [
              {
                type: 'command',
                name: 'run-full-tests'
              }
            ],
            timeout: 1800000 // 30 minutes
          },
          {
            id: 'build-artifacts',
            name: 'Build Release Artifacts',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['test-suite'],
            actions: [
              {
                type: 'command',
                name: 'build-release'
              }
            ]
          },
          {
            id: 'staging-deploy',
            name: 'Deploy to Staging',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['build-artifacts'],
            actions: [
              {
                type: 'command',
                name: 'deploy-staging'
              }
            ]
          },
          {
            id: 'qa-signoff',
            name: 'QA Sign-off',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['staging-deploy'],
            actions: [
              {
                type: 'prompt',
                name: 'qa-checklist'
              }
            ]
          },
          {
            id: 'create-tag',
            name: 'Create Release Tag',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['qa-signoff'],
            actions: [
              {
                type: 'command',
                name: 'create-tag',
                params: {
                  tag: 'v${version}'
                }
              }
            ]
          }
        ]
      }
    });
    
    // 6. 性能优化工作流
    this.register({
      id: 'performance-optimization',
      name: 'Performance Optimization',
      description: 'Systematic performance improvement workflow',
      category: TemplateCategory.MAINTENANCE,
      parameters: [
        {
          name: 'targetArea',
          type: 'string',
          required: true
        },
        {
          name: 'performanceGoal',
          type: 'string',
          default: '20% improvement'
        }
      ],
      definition: {
        id: 'performance-optimization',
        name: 'Optimize: ${targetArea}',
        version: '1.0.0',
        steps: [
          {
            id: 'baseline',
            name: 'Establish Baseline',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: [],
            actions: [
              {
                type: 'command',
                name: 'run-benchmarks',
                params: {
                  area: '${targetArea}'
                }
              }
            ]
          },
          {
            id: 'profile',
            name: 'Profile Application',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['baseline'],
            actions: [
              {
                type: 'command',
                name: 'start-profiler'
              }
            ]
          },
          {
            id: 'identify-bottlenecks',
            name: 'Identify Bottlenecks',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['profile'],
            actions: [
              {
                type: 'command',
                name: 'analyze-profile'
              }
            ]
          },
          {
            id: 'implement-optimizations',
            name: 'Implement Optimizations',
            type: StepType.MANUAL,
            status: StepStatus.PENDING,
            dependencies: ['identify-bottlenecks'],
            actions: [
              {
                type: 'command',
                name: 'create-optimization-branch'
              }
            ]
          },
          {
            id: 'verify-improvements',
            name: 'Verify Improvements',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['implement-optimizations'],
            actions: [
              {
                type: 'command',
                name: 'run-benchmarks',
                params: {
                  compare: true
                }
              }
            ]
          },
          {
            id: 'regression-check',
            name: 'Check for Regressions',
            type: StepType.AUTOMATIC,
            status: StepStatus.PENDING,
            dependencies: ['verify-improvements'],
            actions: [
              {
                type: 'command',
                name: 'run-regression-tests'
              }
            ]
          }
        ]
      }
    });
    
    // 更多模板...
  }
}

// 模板扩展和组合
export class WorkflowTemplateComposer {
  /**
   * 组合多个模板
   */
  static compose(
    templates: WorkflowTemplate[],
    options: {
      name: string;
      description: string;
      mergeStrategy?: 'sequential' | 'parallel';
    }
  ): WorkflowTemplate {
    const composedSteps: WorkflowStep[] = [];
    let stepIdCounter = 0;
    
    for (const template of templates) {
      const templateSteps = template.definition.steps.map(step => ({
        ...step,
        id: `${template.id}_${step.id}`,
        dependencies: step.dependencies.map(dep => `${template.id}_${dep}`)
      }));
      
      if (options.mergeStrategy === 'sequential' && composedSteps.length > 0) {
        // 让新模板的第一步依赖于前一个模板的最后一步
        const lastStep = composedSteps[composedSteps.length - 1];
        templateSteps[0].dependencies.push(lastStep.id);
      }
      
      composedSteps.push(...templateSteps);
    }
    
    return {
      id: `composed_${Date.now()}`,
      name: options.name,
      description: options.description,
      category: TemplateCategory.DEVELOPMENT,
      definition: {
        id: `composed_${Date.now()}`,
        name: options.name,
        version: '1.0.0',
        steps: composedSteps
      }
    };
  }
  
  /**
   * 继承模板
   */
  static extend(
    baseTemplate: WorkflowTemplate,
    extensions: {
      additionalSteps?: WorkflowStep[];
      overrideSteps?: Record<string, Partial<WorkflowStep>>;
      parameters?: TemplateParameter[];
    }
  ): WorkflowTemplate {
    const extendedDefinition = { ...baseTemplate.definition };
    
    // 应用步骤覆盖
    if (extensions.overrideSteps) {
      extendedDefinition.steps = extendedDefinition.steps.map(step => {
        const override = extensions.overrideSteps![step.id];
        return override ? { ...step, ...override } : step;
      });
    }
    
    // 添加额外步骤
    if (extensions.additionalSteps) {
      extendedDefinition.steps.push(...extensions.additionalSteps);
    }
    
    return {
      ...baseTemplate,
      id: `${baseTemplate.id}_extended`,
      name: `${baseTemplate.name} (Extended)`,
      parameters: [...(baseTemplate.parameters || []), ...(extensions.parameters || [])],
      definition: extendedDefinition
    };
  }
}